<template>
  <div ref="root" class="spreadsheet-thumb-preview">
    <div v-if="loading" class="spreadsheet-thumb-preview__loading">
      <b-spinner small variant="secondary" />
    </div>
    <div
      v-else-if="rows && rows.length"
      class="spreadsheet-thumb-preview__scroll"
    >
      <table class="spreadsheet-thumb-preview__table">
        <tbody>
          <tr
            v-for="(row, rIndex) in rows"
            :key="rIndex"
            :class="{ 'spreadsheet-thumb-preview__header': rIndex === 0 }"
          >
            <td v-for="(cell, cIndex) in row" :key="cIndex">{{ cell }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <b-img
      v-else
      :src="icon"
      alt="Spreadsheet"
      class="spreadsheet-thumb-preview__icon"
    ></b-img>
  </div>
</template>

<script>
import {
  nonImageDocIcon,
  isXlsxFile,
  parseCsvPreviewRows,
  parseXlsxPreviewRows,
} from '@/utils/fileTypeIcons'

// CSV is plain text — safely truncatable anywhere, so a small preview-sized
// cap keeps the request tiny. XLSX is a zip container: truncating it
// mid-file produces a corrupt archive that fails to parse entirely, so it
// must be fetched in full (omitting max_bytes — see MediaController@content).
const CSV_PREVIEW_MAX_BYTES = 16384

/**
 * Compact content preview for card/thumbnail contexts (file list grid, the
 * small thumb row on a file's detail page) — same idea as the full preview
 * modal, just fewer rows/columns and no loading spinner chrome. Falls back
 * to the bundled csv-file.svg/xlsx-file.svg icon if the fetch/parse fails
 * for any reason (empty file, deleted media, unsupported workbook, etc).
 *
 * Lazy: doesn't fetch until this component's root element actually scrolls
 * into view (same reasoning as this app's existing b-img-lazy for images) —
 * a list of many spreadsheet files would otherwise fire that many
 * authenticated requests the instant the list renders, all at once,
 * regardless of how many are actually visible on screen.
 *
 * Fetches through media/{id}/content (this app's own API) rather than the
 * file's raw S3 url — the S3/Spaces bucket has no CORS policy permitting
 * browser fetch() reads, only simple <img>/<iframe> display. See
 * MediaController@content on the API for the full explanation.
 */
export default {
  props: {
    file: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      rows: null,
      loading: false,
    }
  },

  computed: {
    icon() {
      return nonImageDocIcon(this.file)
    },
  },

  mounted() {
    if (!('IntersectionObserver' in window)) {
      // No observer support: just load immediately rather than never.
      this.load()
      return
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.load()
          this.observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    this.observer.observe(this.$refs.root)
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },

  methods: {
    async load() {
      if (!this.file || !this.file.id) return
      this.loading = true

      const previewOptions = { maxRows: 6, maxCols: 4, maxCellLength: 12 }

      try {
        if (isXlsxFile(this.file)) {
          const { data } = await this.$api.get(
            `media/${this.file.id}/content`,
            { responseType: 'arraybuffer' },
          )
          this.rows = parseXlsxPreviewRows(data, previewOptions)
        } else {
          const { data: text } = await this.$api.get(
            `media/${this.file.id}/content`,
            {
              responseType: 'text',
              params: { max_bytes: CSV_PREVIEW_MAX_BYTES },
            },
          )
          this.rows = parseCsvPreviewRows(text, previewOptions)
        }
      } catch (err) {
        this.rows = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.spreadsheet-thumb-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fff;
}

.spreadsheet-thumb-preview__loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.spreadsheet-thumb-preview__scroll {
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 0.35rem;
}

.spreadsheet-thumb-preview__table {
  border-collapse: collapse;
  font-size: 0.65rem;
  line-height: 1.2;
  white-space: nowrap;
  pointer-events: none;

  td {
    border: 1px solid #e1e6ea;
    padding: 0.15rem 0.3rem;
  }
}

.spreadsheet-thumb-preview__header {
  font-weight: 700;
  background: #f5f7f9;
}

.spreadsheet-thumb-preview__icon {
  max-width: 40%;
  max-height: 60%;
}
</style>
